import React, { useState, useEffect, useRef, ReactNode } from "react";
import { GoChevronDown } from "react-icons/go";
import { CgSpinner } from "react-icons/cg";
import classNames from "classnames";
import { FormikErrors, FormikTouched } from "formik";
import { twMerge } from "tailwind-merge";
import { Option } from "../../types";

interface DropdownProps<T> {
  id: string;
  label?: string;
  placeholder?: string;
  options?: Option[];
  value?: string;
  className?: string;
  isFetching?: boolean;
  errors?: FormikErrors<T>;
  touched?: FormikTouched<T>;
  setFieldValue: (field: string, value: string) => void;
  setFieldTouched?: (field: string, touched: boolean) => void;
  disabled?: boolean;
}

const Dropdown: React.FC<DropdownProps<any>> = ({
  id,
  placeholder = "Select option",
  options = [],
  value,
  label,
  className,
  isFetching = false,
  errors = {},
  touched = {},
  setFieldValue,
  setFieldTouched,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close on click outside
  useEffect(() => {
    const handler = (event: MouseEvent) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        if (isOpen && setFieldTouched) {
          setFieldTouched(id, true);
        }
      }
    };

    document.addEventListener("mousedown", handler);
    return () => {
      document.removeEventListener("mousedown", handler);
    };
  }, [isOpen, id, setFieldTouched]);

  const handleToggle = () => {
    if (!disabled) {
      setIsOpen((prev) => !prev);
    }
  };

  const handleOptionClick = (option: Option) => {
    setFieldValue(id, option.value);
    setIsOpen(false);
    if (setFieldTouched) {
      setFieldTouched(id, true);
    }
  };

  const selectedOption = options.find((opt) => opt.value === value);
  const hasError = errors[id] && touched[id];

  return (
    <div
      ref={containerRef}
      className={twMerge("w-full flex flex-col gap-1.5", className)}
    >
      {label && (
        <label
          htmlFor={id}
          className="block text-sm font-semibold text-gray-700"
        >
          {label}
        </label>
      )}

      <div className="relative">
        <button
          type="button"
          onClick={handleToggle}
          disabled={disabled}
          className={classNames(
            "relative w-full cursor-pointer rounded-lg border py-2.5 pl-3 pr-10 text-left shadow-sm focus:outline-none transition-all duration-200 ease-in-out sm:text-sm",
            {
              // Default state (not error, not disabled)
              "bg-white border-gray-300 hover:border-gray-400 focus:border-primary focus:ring-2 focus:ring-primary/20":
                !hasError && !disabled,
              
              // Error state
              "bg-white border-red-300 text-red-900 focus:border-red-500 focus:ring-2 focus:ring-red-200":
                hasError,
              
              // Disabled state
              "bg-gray-50 text-gray-400 cursor-not-allowed border-gray-200":
                disabled,
            }
          )}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          aria-labelledby={label ? `${id}-label` : undefined}
        >
          <span className={classNames("block truncate", { "text-gray-500": !selectedOption && !hasError })}>
            {selectedOption ? selectedOption.label : placeholder}
          </span>
          <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
            {isFetching ? (
              <CgSpinner className="h-5 w-5 animate-spin text-primary" />
            ) : (
              <GoChevronDown
                className={classNames("h-4 w-4 text-gray-400 transition-transform duration-200", {
                  "rotate-180 text-primary": isOpen,
                })}
                aria-hidden="true"
              />
            )}
          </span>
        </button>

        {isOpen && !disabled && (
          <div
            className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-lg bg-white py-1 text-base shadow-lg border border-gray-100 ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm animate-fadeIn"
          >
            {isFetching ? (
              <div className="flex items-center justify-center py-4 text-gray-500">
                <CgSpinner className="h-5 w-5 animate-spin mr-2 text-primary" />
                <span>Loading options...</span>
              </div>
            ) : options.length === 0 ? (
              <div className="py-3 px-3 text-center text-gray-500 italic text-sm">
                No options found
              </div>
            ) : (
              <ul role="listbox">
                {options.map((option) => (
                  <li
                    key={option.value}
                    className={classNames(
                      "relative cursor-pointer select-none py-2.5 pl-3 pr-9 transition-colors duration-150 group",
                      {
                        "bg-primary/5 text-primary font-medium": option.value === value,
                        "text-gray-700 hover:bg-gray-50 hover:text-primary": option.value !== value,
                      }
                    )}
                    onClick={() => handleOptionClick(option)}
                    role="option"
                    aria-selected={option.value === value}
                  >
                    <span className="block truncate">
                      {option.label}
                    </span>
                    
                    {option.value === value && (
                      <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-primary">
                        <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      {hasError && (
        <p className="mt-1 text-xs text-red-500 font-medium" id={`${id}-error`}>
          {errors[id] as ReactNode}
        </p>
      )}
    </div>
  );
};

export default Dropdown;
